import { useEffect, useState } from "react";
import {  memberService, groupService } from "../../services";
import { PageHeader } from "../../components/feature/PageHeader";
import { Card } from "../../components/base/Card";
import { Button } from "../../components/base/Button";
import { Badge } from "../../components/base/Badge";
import { Alert } from "../../components/base/Alert";
import { Tabs } from "../../components/base/Tabs";
import { StatCard } from "../../components/base/StatCard";
import { EmptyState } from "../../components/base/EmptyState";
import { Skeleton } from "../../components/base/Skeleton";
import { ErrorState } from "../../components/base/ErrorState";
import { CurrencyDisplay } from "../../components/currency/CurrencyDisplay";
import { formatINR, formatDate, formatDateTime, formatLongDate } from "../../utils/format";
import { FilterBar } from "../../components/feature/FilterBar";
import { Select } from "../../components/base/Select";
import { DateRangePicker } from "../../components/base/DateRangePicker";
import { Stepper } from "../../components/feature/Stepper";
import { PrintLayout } from "../../components/feature/PrintLayout";
import { TextField } from "../../components/base/TextField";
import { ControlsSection } from "./sections/ControlsSection";
import { OverlaysSection } from "./sections/OverlaysSection";
import { DataTableSection } from "./sections/DataTableSection";

function SectionTitle({ children }: { children: string }) {
  return <h2 className="mb-3 mt-6 text-sm font-semibold uppercase tracking-wide text-foreground-500">{children}</h2>;
}

export default function GalleryPage() {
  const [tab, setTab] = useState("overview");
  const [skeletonMode, setSkeletonMode] = useState(false);
  const [retried, setRetried] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterRange, setFilterRange] = useState<{ start: string | null; end: string | null }>({ start: null, end: null });

  const [activeGroupsCount, setActiveGroupsCount] = useState<number>(24);
  const [totalMembersCount, setTotalMembersCount] = useState<number>(1248);
  const [sampleMemberName, setSampleMemberName] = useState<string>("Aarav Mehta");
  const [sampleGroupName, setSampleGroupName] = useState<string>("Laxmi-12");

  useEffect(() => {
    let active = true;
    Promise.all([
      groupService.listGroups().catch(() => []),
      memberService.listMembers().catch(() => []),
    ]).then(([groups, members]) => {
      if (!active) return;
      if (groups && groups.length > 0) {
        const count = groups.filter((g) => g.status === "active").length || groups.length;
        setActiveGroupsCount(count);
        if (groups[0]?.name) setSampleGroupName(groups[0].name);
      }
      if (members && members.length > 0) {
        setTotalMembersCount(members.length);
        if (members[0]?.fullName) setSampleMemberName(members[0].fullName);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const stepperSteps = [
    {
      id: "details",
      title: "Group details",
      subtitle: "Name & terms",
      content: <TextField label="Group name" placeholder="e.g. Laxmi-12" />,
    },
    {
      id: "members",
      title: "Members",
      subtitle: "Add participants",
      content: <p className="text-sm text-foreground-600">Add the members who will join this chit group (later phase).</p>,
    },
    {
      id: "terms",
      title: "Terms",
      subtitle: "Auction rules",
      content: <p className="text-sm text-foreground-600">Configure the monthly subscription and auction rules (later phase).</p>,
    },
  ];

  const statusOptions = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
    { value: "overdue", label: "Overdue" },
  ];

  const activeFilters =
    (filterQuery ? 1 : 0) + (filterStatus ? 1 : 0) + (filterRange.start ? 1 : 0);

  return (
    <div>
      <PageHeader
        title="Component Gallery"
        subtitle="Every reusable component in every state — the reference for all later phases. Use these instead of rebuilding."
        breadcrumb={<span>Development / Component Gallery</span>}
      />

      <SectionTitle>Buttons</SectionTitle>
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="primary" loading>Loading</Button>
          <Button variant="primary" disabled>Disabled</Button>
          <Button variant="primary" size="sm" icon={<i className="ri-add-line" />}>Small</Button>
          <Button variant="primary" size="lg" icon={<i className="ri-save-line" />}>Large</Button>
          <Button variant="outline" iconOnly aria-label="Edit"><i className="ri-pencil-line" /></Button>
        </div>
      </Card>

      <SectionTitle>Currency & dates (Indian format)</SectionTitle>
      <Card>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground-500">Indian lakh/crore grouping + rupee + 2 decimals + tabular digits</p>
            <CurrencyRow label="1,00,000" value={100000} />
            <CurrencyRow label="10,00,000" value={1000000} />
            <CurrencyRow label="12,34,567.89" value={1234567.89} />
            <CurrencyRow label="negative" value={-25000} tone="danger" />
            <p className="pt-2 text-xs text-foreground-500">Right-aligned by default for financial columns.</p>
          </div>
          <div className="space-y-1.5 text-sm">
            <p className="text-xs font-medium text-foreground-500">Day-month-year, never American</p>
            <p><DateExample date="2026-08-25" formatter={formatDate} label="formatDate" /></p>
            <p><DateExample date="2026-08-25" formatter={formatDateTime} label="formatDateTime" /></p>
            <p><DateExample date={new Date()} formatter={formatLongDate} label="formatLongDate (top bar)" /></p>
          </div>
        </div>
      </Card>

      <SectionTitle>Badges</SectionTitle>
      <Card>
        <div className="flex flex-wrap gap-2">
          <Badge variant="active">Active</Badge>
          <Badge variant="inactive">Inactive</Badge>
          <Badge variant="pending">Pending</Badge>
          <Badge variant="overdue">Overdue</Badge>
          <Badge variant="paid">Paid</Badge>
          <Badge variant="partial">Partial</Badge>
          <Badge variant="completed">Completed</Badge>
          <Badge variant="cancelled">Cancelled</Badge>
          <Badge color="info" dot>Info</Badge>
        </div>
      </Card>

      <SectionTitle>Alerts</SectionTitle>
      <Card>
        <div className="space-y-3">
          <Alert variant="success" title="Saved successfully">The collection entry was recorded to the ledger.</Alert>
          <Alert variant="error" title="Could not save">The server is unreachable. Check your connection and try again.</Alert>
          <Alert variant="warning" title="Overdue members">3 members are overdue for group Ganesh-03.</Alert>
          <Alert variant="info" title="Maintenance scheduled">The system will be offline Sunday 02:00–04:00.</Alert>
        </div>
      </Card>

      <SectionTitle>Stat cards</SectionTitle>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Active Groups" value={activeGroupsCount} trend={3.5} icon="ri-archive-line" iconClass="bg-primary-100 text-primary-700" />
        <StatCard label="Total Members" value={totalMembersCount} trend={1.2} icon="ri-group-line" iconClass="bg-accent-100 text-accent-700" />
        <StatCard label="Collections" value={formatINR(1854000)} trend={8.4} icon="ri-wallet-3-line" iconClass="bg-success-100 text-success-700" tone="success" />
        <StatCard label="Overdue" value={formatINR(213500)} trend={-4.1} icon="ri-time-line" iconClass="bg-warning-100 text-warning-700" tone="warning" />
      </div>

      <SectionTitle>Tabs</SectionTitle>
      <Card>
        <Tabs
          items={[
            { id: "overview", label: "Overview", count: 12 },
            { id: "collections", label: "Collections" },
            { id: "payouts", label: "Payouts" },
            { id: "ledger", label: "Ledger" },
          ]}
          value={tab}
          onChange={setTab}
        />
        <div className="pt-3 text-sm text-foreground-600">Active tab: <span className="font-medium text-foreground-900">{tab}</span></div>
      </Card>

      <SectionTitle>States</SectionTitle>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Empty state">
          <EmptyState
            icon="ri-file-list-3-line"
            title="No records yet"
            description="There are no collection entries for this period. Record your first collection to get started."
            action={<Button size="sm" variant="outline" icon={<i className="ri-add-line" />}>Record collection</Button>}
          />
        </Card>
        <Card title={`Skeleton (${skeletonMode ? "showing" : "hidden"})`}>
          {skeletonMode ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : (
            <div className="flex h-24 items-center justify-center text-sm text-foreground-500">Loaded content — no layout shift while loading.</div>
          )}
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setSkeletonMode((v) => !v)}>
            {skeletonMode ? "Show loaded state" : "Simulate loading"}
          </Button>
        </Card>
        <Card title="Error state">
          <ErrorState
            title={retried ? "It's working now" : "Failed to load collections"}
            message={retried ? "The retry action reset the error state." : "We couldn't load this period's collections. This is usually a temporary connection issue."}
            onRetry={() => setRetried(true)}
          />
        </Card>
      </div>

      <SectionTitle>Filter bar</SectionTitle>
      <Card>
        <FilterBar
          search={{ value: filterQuery, onChange: setFilterQuery, placeholder: "Search collections…" }}
          filters={[
            {
              id: "status",
              control: (
                <Select value={filterStatus} onChange={setFilterStatus} placeholder="All statuses" options={statusOptions} />
              ),
            },
            { id: "range", control: <DateRangePicker value={filterRange} onChange={setFilterRange} /> },
          ]}
          activeCount={activeFilters}
          onClear={() => {
            setFilterQuery("");
            setFilterStatus(null);
            setFilterRange({ start: null, end: null });
          }}
          rightActions={<Button size="sm" icon={<i className="ri-download-2-line" />}>Export</Button>}
        />
      </Card>

      <SectionTitle>Wizard / stepper</SectionTitle>
      <Card>
        <Stepper
          steps={stepperSteps}
          validateStep={(i) => (i === 0 ? null : null)}
          onComplete={() => {}}
        />
      </Card>

      <SectionTitle>Print layout</SectionTitle>
      <PrintLayout
        title="Collection Receipt"
        meta={`Receipt #R-1024 · ${formatDate(new Date())}`}
        actions={<Button size="sm" variant="outline" icon={<i className="ri-download-line" />}>Download</Button>}
      >
        <div className="space-y-3 text-sm">
          <div className="flex justify-between border-b border-background-100 pb-2">
            <span className="text-foreground-500">Member</span>
            <span className="font-medium text-foreground-900">{sampleMemberName}</span>
          </div>
          <div className="flex justify-between border-b border-background-100 pb-2">
            <span className="text-foreground-500">Group</span>
            <span className="font-medium text-foreground-900">{sampleGroupName}</span>
          </div>
          <div className="flex justify-between border-b border-background-100 pb-2">
            <span className="text-foreground-500">Amount</span>
            <CurrencyDisplay amount={100000} />
          </div>
          <p className="pt-2 text-xs text-foreground-500">Printed from the app. Click Print to see the clean A4 output (chrome hidden).</p>
        </div>
      </PrintLayout>

      <SectionTitle>Form controls</SectionTitle>
      <ControlsSection />

      <SectionTitle>Overlays & feedback</SectionTitle>
      <OverlaysSection />

      <SectionTitle>Data table (all features)</SectionTitle>
      <DataTableSection />
    </div>
  );
}

function CurrencyRow({ label, value, tone }: { label: string; value: number; tone?: "danger" | "success" }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md bg-background-100/50 px-3 py-1.5">
      <span className="text-xs text-foreground-500">{label}</span>
      <CurrencyDisplay amount={value} tone={tone} />
    </div>
  );
}

function DateExample({ date, formatter, label }: { date: string | Date; formatter: (d: string | Date) => string; label: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md bg-background-100/50 px-3 py-1.5">
      <span className="tnum text-xs text-foreground-500">{label}</span>
      <span className="tnum">{formatter(date)}</span>
    </div>
  );
}